import { useState } from 'react';
import type { ArtifactVendor } from '../types.ts';
import { groupProposedByProduct, splitVendorFeatures } from '../lib/artifact.ts';
import { describeActiveFilters, featureMatchesQuery, textMatches, type FilterState } from '../lib/filters.ts';
import { FeatureExpansion } from './FeatureExpansion.tsx';

/**
 * Per vendor "not compared" expansion: compared:false features (spec §3), plus the totality
 * invariant's proposed-only and dropped entries (docs/specs/app.md, "Artifact totality
 * invariant" — they must survive into the artifact, and the viewer "may render them subtly").
 *
 * v1.1: search + facet filtered. The vendor pivot's unified list is the primary place a facet
 * like "proposed"/"dropped" makes sense; here, that facet narrows which of this section's three
 * blocks show (feature list / proposed line / dropped list) rather than reshaping the section,
 * since this section's whole reason to exist is "the not-compared residue".
 */
export function NotComparedList({ vendor, filters }: { vendor: ArtifactVendor; filters: FilterState }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const { notCompared } = splitVendorFeatures(vendor);

  const showFeatures = filters.facet === 'all' || filters.facet === 'not_compared';
  const showProposed = filters.facet === 'all' || filters.facet === 'proposed';
  const showDropped = filters.facet === 'all' || filters.facet === 'dropped';
  // Verdict filter never matches not-compared/proposed/dropped items — none carry a verdict
  // (same rule as vendorListItemMatchesVerdicts in lib/filters.ts).
  const verdictExcludes = filters.verdicts.length > 0;

  const filteredFeatures =
    showFeatures && !verdictExcludes ? notCompared.filter((f) => featureMatchesQuery(f, filters.query)) : [];
  const filteredProposed =
    showProposed && !verdictExcludes
      ? vendor.proposed.filter((p) => textMatches(p.name, filters.query) || textMatches(p.product, filters.query))
      : [];
  const filteredDropped =
    showDropped && !verdictExcludes
      ? vendor.dropped.filter(
          (d) => textMatches(d.name, filters.query) || textMatches(d.product, filters.query) || textMatches(d.reason, filters.query),
        )
      : [];

  const totalShown = filteredFeatures.length + filteredProposed.length + filteredDropped.length;
  const totalAll = notCompared.length + vendor.proposed.length + vendor.dropped.length;
  const visibleKeys = filteredFeatures.map((f) => `${f.product}::${f.name}`);

  return (
    <div className="panel not-compared">
      <button className="not-compared-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="disclosure" aria-hidden="true">{open ? '▾' : '▸'}</span>
        <span className="not-compared-name">{vendor.company}</span>
        <span className="not-compared-count">
          not compared · {totalShown === totalAll ? totalAll : `${totalShown} of ${totalAll}`}
        </span>
      </button>
      {open && (
        <div className="not-compared-body">
          {totalShown > 1 && (
            <div className="expand-all-row">
              <button onClick={() => setExpanded(new Set(visibleKeys))}>Expand all</button>
              <button onClick={() => setExpanded(new Set())}>Collapse all</button>
            </div>
          )}

          {showFeatures &&
            (filteredFeatures.length === 0 ? (
              <p className="muted">
                {notCompared.length === 0
                  ? 'every feature found was cited by at least one axis'
                  : `nothing matches: ${describeActiveFilters(filters)}`}
              </p>
            ) : (
              // Feature names recur across a vendor's products (e.g. two products both naming a
              // feature "Pre-built scrapers") — the anchor is (product, name), not name alone,
              // same as everywhere else cell/entry resolution cares about product (real sample
              // data: brightdata's 117 not-compared features hit this repeatedly).
              filteredFeatures.map((f) => {
                const key = `${f.product}::${f.name}`;
                return (
                  <FeatureExpansion
                    key={key}
                    feature={f}
                    open={expanded.has(key)}
                    onToggle={() =>
                      setExpanded((prev) => {
                        const next = new Set(prev);
                        next.has(key) ? next.delete(key) : next.add(key);
                        return next;
                      })
                    }
                    highlightQuery={filters.query}
                  />
                );
              })
            ))}

          {showProposed && filteredProposed.length > 0 && (
            <div className="proposed-line">
              <span className="muted">named but never described:</span>
              {groupProposedByProduct(filteredProposed).map((g) => (
                <p key={g.product} className="muted">
                  <em>{g.product}</em>: {g.names.join(', ')}
                </p>
              ))}
            </div>
          )}

          {showDropped && filteredDropped.length > 0 && (
            <details className="dropped-list" open={filters.facet === 'dropped'}>
              <summary className="muted">dropped during adjudication ({filteredDropped.length})</summary>
              <ul>
                {filteredDropped.map((d, i) => (
                  <li key={i} className="muted">
                    <strong>{d.name}</strong> ({d.product}) — {d.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
